// Loading the day, moving between the three days, and ticking things off.
//
// One request per screen: `GET /days/:day` arrives with recurrence expanded,
// completions joined, progress computed and sections ordered, so nothing here
// assembles a screen out of several calls (web-architecture.md section 7.4).
//
// Every day is one entry keyed by the day itself, holding whichever of the three
// states it is in: being read, read, or could not be read. A day therefore
// answers only for itself - one day's payload can never land on another, and one
// day's reason for not arriving can never be shown against another - and a day
// with no entry is a day nothing has asked for yet, which is the whole of the
// rule for when a request goes out.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { DayResponse, IsoDay } from '../api/client.ts';
import {
  completionTargets,
  findItem,
  itemDone,
  withItemDone,
  withReminderDone,
  withStepDone,
} from './day-model.ts';
import { isAfter, parseIsoDay } from '../iso-day.ts';
import type { DayChoice } from './DayScreen.tsx';
import { NOTICE_MS } from '../notice.ts';

/**
 * How long a transient message stays on screen, and therefore how long an Undo
 * can be reached (captain's decision, 2026-08-17).
 *
 * Ten seconds rather than the prototype's 5.2. Undo on a destructive action is
 * a correctness requirement in this product rather than a nicety - silent data
 * loss is the single thing this category is punished for (AGENTS.md) - and 5.2
 * seconds is long enough to SEE a toast and not long enough to notice it, read
 * it, decide and press it on a phone. A safety net nobody can reach in time is
 * not a safety net. It is shorter still in practice than it looks: after a
 * create the line waits for the day to be read back before it is even said.
 *
 * Anything that comes to depend on this duration must import this constant
 * rather than write the number down again.
 */
export { NOTICE_MS };

/** The literal the server resolves in the user's stored zone (section 7.4). */
const TODAY = 'today';

/** Said when the payload does not name the day a pill points at. */
const UNNAMED_NEIGHBOUR = 'The server did not say which day that is.';

/** One frozen empty set, so a day with nothing pinned is the same value twice. */
const NO_PINS: ReadonlySet<string> = new Set();

type DayKey = IsoDay | typeof TODAY;

type DayEntry =
  | { status: 'loading' }
  | { status: 'loaded'; payload: DayResponse }
  | { status: 'failed'; message: string };

/**
 * The completion writes made against ONE day: how many are unsettled right now,
 * and how many have been started at all.
 *
 * Two numbers rather than one because they answer different questions. `out`
 * answers "is a write to this day unconfirmed", which a read landing now would
 * overwrite; `begun` answers "did a write to this day start while this read was
 * in the air", which is the same hazard with the two events in the other order
 * and is invisible to `out` when the write settles inside the round trip.
 */
interface DayWrites {
  out: number;
  begun: number;
}

type PendingRefresh = 'none' | 'refresh' | 'today';

/**
 * A line at the bottom of the screen, and the way back from it.
 *
 * `undo` is what makes archiving a schedule or an item safe to press: silent
 * data loss is the single thing this product category is punished for, so undo
 * on a destructive action is a correctness requirement here rather than a
 * feature (AGENTS.md). It is absent on the lines that are only news - a failed
 * tick, something saved - and on the one destruction that genuinely cannot be
 * taken back, which is why deleting forever asks first instead.
 */
export interface Notice {
  message: string;
  undo?: () => void;
}

interface ActiveNotice {
  id: number;
  value: Notice;
  expiresAt: number;
  retire?: () => void;
}

/**
 * Whether the payload answers the day it was asked for, in the form this screen
 * reads days in. A payload that does not is reported rather than cached: it
 * would be filed under a key nothing looks up, and the day asked for would still
 * be missing. A `date` column serialised as a timestamp is what this looks like
 * from here, so the specifics go to the console for whoever is building the
 * server, and the screen says the plain thing.
 */
function contractProblem(payload: DayResponse, requested: DayKey): string | null {
  if (parseIsoDay(payload.day) === null) {
    console.warn(
      `Perfect Note: the day payload names ${JSON.stringify(payload.day)}, which is not a YYYY-MM-DD calendar date.`,
    );
    return 'The server sent a day in a form this screen cannot read.';
  }
  if (requested !== TODAY && payload.day !== requested) {
    console.warn(
      `Perfect Note: asked for ${requested} and the server answered with ${payload.day}.`,
    );
    return 'The server sent a different day from the one this screen asked for.';
  }
  return null;
}

/**
 * Which day the chooser is pointing at.
 *
 * `unnamed` is the state that matters. The day either side of today is the
 * PAYLOAD'S to name, and one it does not name is not today - it is nothing. The
 * fallback that used to stand here quietly made it today, so the Tomorrow pill
 * drew today's rows and a tick on one of them recorded a completion against
 * today: work written into the wrong day of the captain's own record, with
 * nothing on screen to say so. Saying the day is not here is the only honest
 * answer, and it is why the cases below are a union rather than a nullable day
 * with a default.
 *
 * `resolving` keeps two questions apart that a single day cannot answer at once:
 * which day to ASK for, and which day to NAME on screen. A neighbouring pill
 * with today's payload missing has to ask for TODAY, because today's payload is
 * the only thing that can say which day that pill means - but it must name
 * nothing while it does, or the page heads itself with today's date under a
 * pressed Tomorrow pill. That is the same silent fallback as before, one render
 * wide, and it is reachable exactly where it hurts: the retry after a payload
 * failed to name a neighbour.
 */
type Target =
  | { status: 'unknown' }
  | { status: 'resolving'; ask: IsoDay }
  | { status: 'day'; day: IsoDay }
  | { status: 'unnamed'; field: 'previousDay' | 'nextDay'; named: unknown };

/**
 * Which day a request goes out for, and NOT which day is named: `unnamed` asks
 * for nothing because there is nothing to ask for, and `resolving` asks for
 * today while naming none of it.
 */
function keyFor(target: Target): DayKey | null {
  switch (target.status) {
    case 'unknown':
      return TODAY;
    case 'resolving':
      return target.ask;
    case 'day':
      return target.day;
    case 'unnamed':
      return null;
  }
}

export interface DayScreenState {
  /** The day being looked at, once it is known and loaded. */
  day: DayResponse | null;
  /** The day being looked at, known before it has loaded and while it fails. */
  dayKey: IsoDay | null;
  /**
   * The ACCOUNT'S today, resolved server-side, or null before the first payload
   * lands. Not the day being looked at.
   *
   * Exposed because a screen outside the day needs it: the reminders list has
   * no day of its own and a tick there is recorded against the day it was
   * done - and no client works out the account's day (AGENTS.md), so the only
   * honest source is the payload that already carries one.
   */
  today: IsoDay | null;
  /**
   * Whether there is a day to move FROM, which is the only thing the chooser
   * needs. Deliberately not `dayKey !== null`: a pill whose day the payload
   * never named has no `dayKey`, and disabling the chooser there would strand
   * the user on the one choice the screen cannot show.
   */
  ready: boolean;
  /** Whether the day being looked at is on its way. */
  loading: boolean;
  /** Set only when the day being looked at could not be read. */
  failure: string | null;
  /**
   * Nobody is signed in. Not a kind of failure: it is a state of the SESSION
   * rather than of a day, so it is answered by the signed-out screen rather than
   * by a message where the day would be. Once true it stays true - signing in is
   * a navigation off this page, and every later request would 401 the same way.
   */
  signedOut: boolean;
  /**
   * This account has never made its first-run choice, so the FIRST-RUN GATE is
   * the screen rather than the day. Read off the day payload, which is the first
   * request this client makes.
   *
   * Held as its own state rather than derived from `day`, and lowered only by a
   * later payload saying so - which is what keeps the gate up from the moment
   * the choice is sent until the day it produced has actually been read back. A
   * gate that came down on the write succeeding would show an empty day for as
   * long as the re-read took, on the one screen where an empty day is the exact
   * thing being fixed.
   *
   * Distinct from `signedOut` in both directions: the person IS signed in, and
   * unlike that flag this one is lowered, once, by the payload that follows the
   * choice.
   */
  firstRun: boolean;
  /** A transient line, and the way back from it when there is one. */
  notice: Notice | null;
  /** Whether the day on screen is after the server's today. */
  future: boolean;
  choice: DayChoice;
  choose: (choice: DayChoice) => void;
  activateToday: () => void;
  retry: () => void;
  toggleItem: (itemId: string) => void;
  toggleStep: (itemId: string, stepId: string) => void;
  /** Ticks a reminder off the day on screen, or takes the tick back. */
  toggleReminder: (reminderId: string) => void;
  /**
   * The reminders ticked in this sitting, ON THE DAY ON SCREEN.
   *
   * The block draws two rows, and a ticked one would otherwise drop straight
   * out of them - vanishing under the finger that ticked it, with the Undo
   * gone with it. These stay drawn, struck through, until the day is left.
   * Keyed by day rather than kept across the chooser: a pin belongs to a
   * sitting on one day, and yesterday's block is about yesterday.
   */
  pinnedReminders: ReadonlySet<string>;
  /**
   * Puts a reminder JUST CREATED into the block's visible set, on the day it
   * lands on - which is the account's today, because that is what a reminder is
   * anchored to (`createReminder` in `edit/useEditor.ts`).
   *
   * The approved design pins a reminder created in this sitting exactly as it
   * pins one ticked in this sitting (prototype-v2.md section 1.2). Without it a
   * new reminder usually sorts below the two rows the block draws - undated ones
   * sort last - so the only thing that moved on the screen that made it was the
   * footer count, which is a successful write whose result cannot be seen.
   */
  pinReminder: (reminderId: string) => void;
  say: (message: string, undo?: () => void, retire?: () => void) => void;
  amendNotice: (undo: () => void, message: string) => boolean;
  /**
   * Takes the line away before it times out.
   *
   * Said by an Undo as it runs: the line it is on is the only thing offering
   * that button, and a button still on screen while the request it sends is in
   * flight is a button that can be pressed twice - the second one arriving at a
   * schedule already restored, or an id already gone, and reported as "That
   * could not be undone" for something that was.
   */
  dismiss: (undo?: () => void) => void;
  /**
   * Every day this screen holds is out of date - said after something was
   * created, changed, archived or restored, by every writer in this shell.
   *
   * It does NOT empty the cache. A day that is on screen stays on screen while
   * its replacement is fetched, because emptying it would put "Loading your
   * day…" over the whole page every time an item is added - a flash of nothing
   * in exchange for no information. The days that are not on screen are re-read
   * when they are next looked at, which is the same freshness for no request.
   *
   * AND NO READ IS EVER APPLIED THAT WOULD CLOBBER AN UNSETTLED TICK ON THE DAY
   * IT ANSWERS FOR, at both ends: one asked for while a tick is in flight is
   * held over until that tick settles, and one already in the air when the tick
   * was made is dropped and asked for again. Either way it would replace the
   * payload the tick was painted into and un-tick the row. So a caller gets its
   * read and none of them carries the invariant. See the definition and `load()`
   * below, and the cost recorded at the definition.
   */
  refresh: () => void;
  /**
   * Which round of reads everything belongs to. Bumped by `refresh()`.
   *
   * Exposed because a screen outside the day can hold a read of its own - the
   * account's schedules, in Settings, which no day payload carries - and one
   * write has to reach both. Watching this is how that screen re-reads without
   * a second `refresh` mechanism to keep in step with this one.
   */
  revision: number;
  /**
   * Bumped whenever a tick reaches the server. Deliberately NOT `revision`: that
   * one re-reads this screen's own day and would undo the optimistic update that
   * makes a tick instant, so what watches this is a screen outside the day whose
   * figures are DERIVED from the completion log - the month's counts.
   */
  completions: number;
  /**
   * Raised by a write from outside this hook that found no session, so a 401
   * during a create or an edit lands on the same screen a 401 during a tick
   * does. Never lowered - see `signedOut`.
   */
  signOut: () => void;
}

export function useDayScreen(): DayScreenState {
  // The server's today, taken from the payload rather than from this machine's
  // clock: `GET /days/today` resolves it in the user's stored zone, so no client
  // ever has to agree with the server about what day it is.
  const [today, setToday] = useState<IsoDay | null>(null);
  const [choice, setChoice] = useState<DayChoice>('today');
  const [byDay, setByDay] = useState<Record<DayKey, DayEntry>>({});
  const [notice, setNotice] = useState<Notice | null>(null);
  const activeNotice = useRef<ActiveNotice | null>(null);
  const nextNoticeId = useRef(0);

  const retireNotice = useCallback((id?: number) => {
    const current = activeNotice.current;
    if (current === null || (id !== undefined && current.id !== id)) return;
    activeNotice.current = null;
    setNotice(null);
    current.retire?.();
  }, []);

  const say = useCallback((message: string, undo?: () => void, retire?: () => void) => {
    const previous = activeNotice.current;
    const value = undo === undefined ? { message } : { message, undo };
    activeNotice.current = {
      id: (nextNoticeId.current += 1),
      value,
      expiresAt: Date.now() + NOTICE_MS,
      ...(retire === undefined ? {} : { retire }),
    };
    setNotice(value);
    previous?.retire?.();
  }, []);

  const amendNotice = useCallback((undo: () => void, message: string): boolean => {
    const current = activeNotice.current;
    if (current?.value.undo !== undo) return false;
    const value = { ...current.value, message };
    activeNotice.current = { ...current, value };
    setNotice(value);
    return true;
  }, []);

  const dismiss = useCallback(
    (undo?: () => void) => {
      const current = activeNotice.current;
      if (undo !== undefined && current?.value.undo !== undo) return;
      retireNotice();
    },
    [retireNotice],
  );

  // Which round of reads the cache belongs to. Everything written or archived
  // through the sheets bumps it, and a day read in an earlier round is stale -
  // shown while it is replaced rather than thrown away. See `refresh` above.
  const [generation, setGeneration] = useState(0);
  const [completions, setCompletions] = useState(0);
  const loadedIn = useRef<Map<DayKey, number>>(new Map());

  // Which reminders have been ticked or created in this sitting, BY DAY. Held
  // with their day rather than cleared by an effect when the chooser moves: an
  // effect would have to fire between the day changing and the block being
  // drawn from it, and a pin that outlived its day by one render is a row from
  // yesterday pinned onto today.
  //
  // A day each, rather than one day at a time, because the two things that pin
  // do not name the same day: a tick pins the day on SCREEN, and a create pins
  // the account's TODAY, which is where a reminder lands whatever the chooser
  // is on. One shared set would have a reminder made from Yesterday silently
  // drop the row ticked there a moment earlier.
  const [pinned, setPinned] = useState<Record<IsoDay, ReadonlySet<string>>>({});

  const pin = useCallback((dayKey: IsoDay, reminderId: string) => {
    setPinned((held) => ({ ...held, [dayKey]: new Set(held[dayKey] ?? []).add(reminderId) }));
  }, []);

  // Raised by ANY 401, from a read or from a write, and never lowered. A session
  // that has expired mid-use is the same fact as never having had one: whichever
  // request found out first, nothing else on this page can succeed afterwards.
  const [signedOut, setSignedOut] = useState(false);

  // Whether this account still owes its first-run choice, from the day payload -
  // an ACCOUNT-level fact that every day of that account carries, so the newest
  // read wins whichever day it was for.
  //
  // FALSE UNTIL A PAYLOAD SAYS OTHERWISE, deliberately: the alternative is a
  // welcome screen flashed at somebody with a year of history while their day
  // loads. The cost of being wrong this way round is one render of the day's own
  // loading state, which is the screen they were going to see anyway.
  const [firstRun, setFirstRun] = useState(false);

  // Which days have a request out RIGHT NOW. The cache below cannot answer that
  // on its own: marking a day as loading is a state update, and StrictMode runs
  // the effect twice within one commit - so the second run still sees the day
  // as unasked-for and sends a duplicate GET. A ref changes within the commit,
  // which is exactly the difference needed. It is development-only behaviour,
  // and it is also every other way one effect could run twice for one day.
  const inFlight = useRef<Set<DayKey>>(new Set());
  const literalResolution = useRef(0);
  const [literalRetry, setLiteralRetry] = useState(0);

  // The completion writes THIS HOOK makes, BY THE DAY THEY WERE MADE AGAINST,
  // and whether a re-read is waiting on them. Kept beside `inFlight` rather than
  // loaded onto it: that one is which days have a READ out, and these are which
  // days have a WRITE out.
  //
  // DO NOT APPLY A READ THAT WOULD CLOBBER AN UNSETTLED WRITE TO THE SAME DAY -
  // the invariant `reminders/useReminders.ts` holds over its own list, in the
  // same two counters, so there is one rule here and not two dialects of it. A
  // day re-read replaces the WHOLE cached payload for that day, and a payload
  // the server assembled before an item's tick landed carries that item as not
  // done: the row un-ticks, the figure drops back, and `toggleItem` deliberately
  // never refreshes, so nothing puts it right again.
  //
  // PER DAY, because a day's payload can only ever replace ITS OWN entry. A tick
  // on today cannot be undone by tomorrow's payload, so measuring tomorrow's
  // read against it protected nothing and cost a whole round trip on screen -
  // the day sat on its loading state until the tick settled.
  //
  // IT TAKES BOTH ENDS, and neither is the other's spare. `refresh` refuses to
  // ASK while a write is unconfirmed; `load()` refuses to APPLY a payload a
  // write to that day could predate. A read asked for when nothing was
  // outstanding is still in the air when the next tick is made, and that
  // ordering is invisible to the asking end - which is exactly how a tick was
  // still being lost after the first half of this guard shipped.
  //
  // `begun` is the half that catches a write which both began AND settled inside
  // one round trip: `out` reads zero at both ends of that read and the payload
  // is stale all the same. "Nothing painted since the read went out" is a
  // different, narrower rule, and `reminders/README.md` records why it is not
  // enough. It is why a day is forgotten below only once nothing can still be
  // measured against it.
  //
  // AND IT RANGES OVER COMPLETION WRITES, which is the whole of what this hook
  // writes. A create or an edit from `edit/useEditor.ts`, and a note saved in
  // `notes/`, are counted here by nothing and never were: nothing paints those
  // onto this payload optimistically, so a read that predates one costs a moment
  // of staleness rather than somebody's tick.
  //
  // KNOWN LIMIT OF DOING THIS CLIENT-SIDE, and not a defect of this
  // implementation: local bookkeeping is a PROXY for whether a payload predates
  // the writes THIS client made. Nothing in the payload says which writes it
  // reflects, so a tick made in another tab, on another device or by a second
  // client is invisible here and cannot be reconciled this way.
  const writes = useRef<Map<IsoDay, DayWrites>>(new Map());
  // The same question asked of the whole account rather than of one day, which
  // is all `refresh` can ask: it names no day, so it cannot be scoped to one.
  const writesOut = useRef(0);
  const pendingRefresh = useRef<PendingRefresh>('none');

  const invalidateToday = useCallback(() => {
    literalResolution.current += 1;
    loadedIn.current.delete(TODAY);
    setByDay((cache) => {
      if (cache[TODAY] === undefined) return cache;
      const next = { ...cache };
      delete next[TODAY];
      return next;
    });
    setToday(null);
  }, []);

  /**
   * Everything this screen holds is out of date - and the read that says so
   * waits, when a tick made here is still unconfirmed.
   *
   * DO NOT APPLY A READ THAT WOULD CLOBBER AN UNSETTLED WRITE. The re-read
   * arrives with the server's answer to every write EXCEPT the ones still in
   * flight and replaces the WHOLE cached payload with it, so the row a tick was
   * painted for un-ticks - and `toggleItem` and `toggleStep` deliberately never
   * refresh, so nothing puts it right again. Held over instead, and released by
   * the last write to settle. `load()` holds the other end of the same rule, for
   * the read that was already in the air when the tick was made, and it is the
   * end that can be scoped to a DAY: this one names no day, so it waits for the
   * account to be quiet.
   *
   * THE GUARD IS HERE RATHER THAN AT EACH CALLER, and there is deliberately no
   * second, unguarded way to ask. Every writer outside this hook reaches the day
   * through this one function - the editor, the board, the reminders list, the
   * first-run choice - and each of them is a place somebody would have to
   * remember an invariant they cannot see from where they are standing. The
   * board was written without it and silently lost a tick made seconds earlier;
   * a screen written next would have lost the same one.
   *
   * DEFERRED, NEVER DROPPED - AS FAR AS THE WRITE SETTLING, which is the
   * accepted cost and not an oversight. The release runs only from inside
   * `write()`, and `request()` in `api/client.ts` sets no timeout and no
   * `AbortSignal`, so a `POST /completions` that is accepted and never answered
   * holds every refresh in this shell until the page is reloaded. The
   * alternative is applying the read, which is the silent loss of somebody's
   * work that all of this exists to close.
   */
  const refresh = useCallback(() => {
    if (writesOut.current > 0) {
      if (pendingRefresh.current !== 'today') pendingRefresh.current = 'refresh';
      return;
    }
    setGeneration((n) => n + 1);
  }, []);

  const target: Target = useMemo(() => {
    if (today === null) return { status: 'unknown' };
    if (choice === 'today') return { status: 'day', day: today };

    // Until today itself is here there is no payload to read a neighbour from,
    // so today is what gets asked for - and this pill still names no day.
    const base = byDay[today];
    if (base?.status !== 'loaded') return { status: 'resolving', ask: today };

    const field = choice === 'yesterday' ? 'previousDay' : 'nextDay';
    const named: unknown = base.payload[field];
    if (typeof named === 'string' && parseIsoDay(named) !== null) {
      return { status: 'day', day: named };
    }
    return { status: 'unnamed', field, named };
  }, [today, choice, byDay]);

  const key: DayKey | null = keyFor(target);
  const entry = key === null ? undefined : byDay[key];

  useEffect(() => {
    if (target.status !== 'unnamed') return;
    // The user is told the plain thing; the value that is not a day goes to
    // whoever is building the server, as a mis-serialised `day` already does.
    console.warn(
      `Perfect Note: the day payload names ${JSON.stringify(target.named)} as ${target.field}, which is not a YYYY-MM-DD calendar date.`,
    );
  }, [target]);

  useEffect(() => {
    // A day with a CURRENT entry has been asked for. That, and nothing about
    // the cache as a whole, is what decides whether a request goes out - so a
    // tick writing to another day cannot restart this one. An entry read before
    // the last write is not current: it is shown, and asked for again.
    if (key === null || inFlight.current.has(key)) return;
    if (entry !== undefined && loadedIn.current.get(key) === generation) return;

    inFlight.current.add(key);
    // Recorded on the way OUT rather than on the way back, so a read that fails
    // is a read this generation has had, and the effect does not re-fire on it
    // for ever. Asking again is `retry`, which forgets the entry.
    loadedIn.current.set(key, generation);
    setByDay((cache) =>
      cache[key] === undefined ? { ...cache, [key]: { status: 'loading' } } : cache,
    );

    const load = async () => {
      // WHICH DAY THIS READ CAN CLOBBER, which is the only day it is measured
      // against. `key` may be the literal `today` rather than a calendar date,
      // while `write()` is always given a real one - and the literal is asked
      // for only while `today` is still unknown, which is before any payload has
      // named a day and therefore before anything can have been ticked. So a
      // read of the literal has no write of its own to be measured against, and
      // that is the one place the `DayKey` union bites here.
      const readDay: IsoDay | null = key === TODAY ? null : key;
      const resolution = key === TODAY ? literalResolution.current : null;
      // Stamped on the way OUT, both of them, exactly as `useReminders` stamps
      // its own before its own await: a write to this day already in flight, and
      // a write to it begun while this read is waiting, each mean the payload
      // that comes back can predate what is on screen.
      const before = readDay === null ? undefined : writes.current.get(readDay);
      const outstanding = before?.out ?? 0;
      const begun = before?.begun ?? 0;
      let superseded = false;
      try {
        const payload = await api.day(key);
        superseded = resolution !== null && resolution !== literalResolution.current;
        if (superseded) return;
        const problem = contractProblem(payload, key);
        if (problem !== null) {
          setByDay((cache) => ({ ...cache, [key]: { status: 'failed', message: problem } }));
          return;
        }

        // THE OTHER END OF THE INVARIANT. A payload the server assembled before
        // a tick to THIS day reached it carries that item as not done, and
        // applying it would replace the cached day the tick was painted into -
        // so the row un-ticks, the figure drops back, and nothing asks again.
        // Dropped instead: nothing is written to the cache, so the day on screen
        // goes on being the one carrying the tick somebody just made.
        const now = readDay === null ? undefined : writes.current.get(readDay);
        const stale = outstanding > 0 || (now?.begun ?? 0) !== begun;
        if (!stale) {
          const loaded: DayEntry = { status: 'loaded', payload };
          // `today` is filed under its own date as well as under the literal,
          // and both are as fresh as each other - so the alias carries the
          // generation too, or looking at today by name would fetch it twice.
          loadedIn.current.set(payload.day, generation);
          setByDay((cache) => ({ ...cache, [key]: loaded, [payload.day]: loaded }));
        }

        // AND THE DAY'S COUNTS ARE FORGOTTEN HERE, once nothing is unsettled
        // against it. They cannot be dropped where the write settles while a
        // read of that day is still in the air, because `begun` is exactly what
        // that read has yet to be measured against - and this is the moment it
        // has been. Two reads of one day are never in flight at once, so there
        // is no other stamp left waiting on these numbers.
        if (readDay !== null && (now?.out ?? 0) === 0) writes.current.delete(readDay);

        // AND THE ACCOUNT'S OWN FACTS ARE KEPT EVEN FROM A DROPPED READ, because
        // no completion write can make either of them wrong. Dropping them would
        // strand the shell rather than protect a tick: a first read dropped with
        // `today` still null leaves the whole screen in its loading state, with
        // the first-run gate unanswered behind it.
        if (key === TODAY) setToday(payload.day);
        // Every payload of this account carries it and they cannot disagree, so
        // the freshest read wins whichever day it was for. This is also what
        // lowers the gate: the read that `refresh()` sends after the choice is
        // the first one that can say false, and it arrives with the content.
        setFirstRun(payload.firstRun);

        // A DROPPED READ IS ASKED FOR AGAIN, through the guarded `refresh`
        // itself rather than a second release path of its own: it defers while a
        // write is unconfirmed and goes out at once when none is, so it cannot
        // strand a day whose write settled during the round trip - which setting
        // `refreshWhenSettled` by hand here would do, since the settled write
        // never runs that check again. This converges: ticking inside every
        // round trip defers again each time, and it settles the moment the
        // ticking stops, exactly as `useReminders` behaves.
        //
        // AND THAT IS THE ONLY THING THAT ASKS AGAIN. The way-out
        // `loadedIn.set(key, generation)` above deliberately stands, dropped
        // payload and all, for the same reason it is written on the way out at
        // all: this generation has had its read. Clearing it here would let the
        // very next optimistic paint re-enter this effect - `entry` changes on
        // every tick - and send a read that is certain to be dropped too, one
        // wasted round trip per tick, on exactly the slow connection this guard
        // exists for.
        if (stale) refresh();
      } catch (error) {
        superseded = resolution !== null && resolution !== literalResolution.current;
        if (superseded) return;
        if (isSignedOut(error)) setSignedOut(true);
        // Filed as a failure even so, which is what stops this day being asked
        // for again: the screen above is about to be replaced, but the effect
        // below re-fires for any day that has no entry, and a 401 that left the
        // cache empty would send the same doomed request every render.
        setByDay((cache) => ({
          ...cache,
          [key]: { status: 'failed', message: messageFor(error) },
        }));
      } finally {
        // Released however it ended, so a day that failed can be asked for
        // again - which is what the chooser and "Try again" both do.
        inFlight.current.delete(key);
        if (superseded) setLiteralRetry((n) => n + 1);
      }
    };

    void load();
  }, [key, entry, generation, refresh, literalRetry]);

  // A day the payload never named is not a day that can be asked for, so there
  // is nothing in flight and nothing cached to show - only the reason.
  const unnamed = target.status === 'unnamed';

  useEffect(() => {
    const current = activeNotice.current;
    if (notice === null || current === null) return;
    const timer = setTimeout(
      () => retireNotice(current.id),
      Math.max(0, current.expiresAt - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [notice, retireNotice]);

  // A payload is only ever shown for the day the screen NAMES, so a day being
  // asked for to resolve a pill cannot be drawn as that pill's own.
  const day = target.status === 'day' && entry?.status === 'loaded' ? entry.payload : null;
  const failure = unnamed ? UNNAMED_NEIGHBOUR : entry?.status === 'failed' ? entry.message : null;
  const loading = !unnamed && (entry === undefined || entry.status === 'loading');

  // What "try again" asks for. When the payload did not name the day a pill
  // points at, the thing worth asking for again is the payload that failed to
  // name it, not the day it failed to name.
  const askAgainFor: DayKey = key ?? today ?? TODAY;

  const updateDay = useCallback((dayKey: IsoDay, to: (cached: DayResponse) => DayResponse) => {
    setByDay((cache) => {
      const cached = cache[dayKey];
      if (cached?.status !== 'loaded') return cache;
      return { ...cache, [dayKey]: { status: 'loaded', payload: to(cached.payload) } };
    });
  }, []);

  const forget = useCallback((dayKey: DayKey) => {
    setByDay((cache) => {
      if (cache[dayKey] === undefined) return cache;
      const next = { ...cache };
      delete next[dayKey];
      return next;
    });
  }, []);

  /** Which of `routineIds` did not land. Empty is the whole write succeeding. */
  const write = useCallback(
    async (
      dayKey: IsoDay,
      routineIds: string[],
      done: boolean,
      undo: (failedIds: string[]) => void,
    ): Promise<string[]> => {
      // Raised BEFORE the request, never after it: a refresh asked for in
      // between would find nothing outstanding and go straight out, and a read
      // already in the air would come back looking newer than this tick. Both
      // against the DAY being written, which is the only day a payload carrying
      // this tick can belong to.
      writesOut.current += 1;
      const at = writes.current.get(dayKey) ?? { out: 0, begun: 0 };
      at.out += 1;
      at.begun += 1;
      writes.current.set(dayKey, at);
      // Settled rather than all: the requests that were already in flight when
      // one of them failed still reach the server, so which rows landed has to
      // be read off every result rather than assumed from the first rejection.
      const results = await Promise.allSettled(
        routineIds.map((id) => (done ? api.complete(id, dayKey) : api.uncomplete(id, dayKey))),
      );
      writesOut.current -= 1;
      at.out -= 1;
      // Forgotten as soon as it can be, and no sooner: while ANY read is in the
      // air its stamp may still be waiting to be measured against this day's
      // `begun`, and a count deleted underneath it would read as zero and let a
      // stale payload through. When nothing is being read, nothing can be
      // measured, so the record has no reader left.
      if (at.out === 0 && inFlight.current.size === 0) writes.current.delete(dayKey);
      // Whoever asked while this was out gets their read now, once - and after
      // the response above, so the payload it fetches carries this write.
      if (writesOut.current === 0) {
        const pending = pendingRefresh.current;
        pendingRefresh.current = 'none';
        if (pending === 'today') invalidateToday();
        else if (pending === 'refresh') setGeneration((n) => n + 1);
      }

      const failed = routineIds.filter((_, i) => results[i]?.status === 'rejected');
      // Any row that landed has changed the log, so anything derived from it is
      // stale - a five-step tick where three reached the server included.
      if (routineIds.length - failed.length > 0) setCompletions((n) => n + 1);
      if (failed.length === 0) return failed;

      // Offline writes are out of scope for v1: there is no queue and no retry,
      // so the rows that did not land are put back rather than left looking
      // saved, and the rows that did land stay, because the server has them.
      undo(failed);
      const reasons = results.flatMap((r) =>
        r.status === 'rejected' ? [r.reason as unknown] : [],
      );
      // A tick is the likeliest thing to be doing when a session runs out, so
      // this is the ordinary way the expiry is met rather than an odd one. The
      // undo above still runs: it is what leaves the row showing the server's
      // state, so nothing on the way out claims to have been saved.
      if (reasons.some(isSignedOut)) setSignedOut(true);
      const message = messageFor(reasons[0]);
      say(
        failed.length === routineIds.length
          ? `Not saved. ${message}`
          : `Part of that was not saved. ${message}`,
      );
      return failed;
    },
    [invalidateToday, say],
  );

  const toggleItem = useCallback(
    (itemId: string) => {
      if (day === null) return;
      const item = findItem(day, itemId);
      if (item === null) return;

      const next = !itemDone(item);
      const targets = completionTargets(item, next);
      const dayKey = day.day;

      updateDay(dayKey, (cached) => withItemDone(cached, itemId, next));
      // Exactly the rows that did not land go back to what this tick moved them
      // from, and nothing else is touched: a step the user changed while the
      // write was in flight is the server's state, not this tick's to restore.
      void write(dayKey, targets, next, (failed) =>
        updateDay(dayKey, (cached) =>
          failed.reduce(
            (updated, id) =>
              id === itemId
                ? withItemDone(updated, itemId, !next)
                : withStepDone(updated, itemId, id, !next),
            cached,
          ),
        ),
      );
    },
    [day, updateDay, write],
  );

  /**
   * A reminder is TICKED like anything else and refused like nothing else.
   *
   * The write is the ordinary `POST /completions`, because a reminder earns an
   * ordinary completion row - so the tick, its undo and the idempotent retry
   * are the ones already proven. What is different is what may be ticked: a
   * reminder is finished ONCE, so one already finished on a LATER day is
   * history rather than a control, and the server refuses that tick
   * (`already-finished`). `ReminderRow` draws no tick on such a row; this
   * refuses it a second time, because a row is drawn from a payload that can
   * be a moment out of date and an interface that succeeds at nothing is what
   * this client keeps being caught by.
   */
  const toggleReminder = useCallback(
    (reminderId: string) => {
      if (day === null) return;
      const reminder = day.reminders.find((r) => r.id === reminderId);
      if (reminder === undefined || reminder.finishedLater) return;

      const next = !reminder.done;
      const dayKey = day.day;

      // Pinned before the write, not after it: the row has to stay on screen
      // from the moment it is ticked, including while the request is in flight
      // and including when the request fails - what it says then is the
      // server's state, on a row somebody can still see.
      pin(dayKey, reminderId);

      updateDay(dayKey, (cached) => withReminderDone(cached, reminderId, next));
      void (async () => {
        const failed = await write(dayKey, [reminderId], next, () =>
          updateDay(dayKey, (cached) => withReminderDone(cached, reminderId, !next)),
        );
        // EVERY OTHER CACHED DAY IS NOW STALE, and this is the one tick where
        // that is true. A schedule item's completion is PER-DAY, so ticking it
        // changes the day it was ticked on and leaves the others true - which is
        // why `toggleItem` and `toggleStep` deliberately do not do this, and a
        // day re-read per tick on the daily hot path would be a real regression.
        // A reminder has ONE completion row, and that single row decides how it
        // reads on every day in its window: open, ticked on this day, or
        // finished later. Tick one on Yesterday and today's cached payload still
        // says open, draws a live checkbox, and the server answers 422
        // `already-finished` - a checkbox that always fails, which is the trap
        // this client keeps being caught by.
        //
        // Re-read rather than map the new state across the cache: on another day
        // the right presentation is `finishedLater`, or the reminder is off that
        // day entirely, and deriving that here would be re-implementing
        // `assembleDay`'s reminder window in the client. The server is the only
        // thing that knows. This is the mirror of `useReminders.toggle`, which
        // refreshes so the day's block catches up with a tick made on the list;
        // the two together are what keep the surfaces agreeing about one row.
        //
        // Only on success: a write that failed changed nothing to catch up with,
        // and the undo above has already put the row back. And not while another
        // tick is still out - `refresh` holds that off itself, because this
        // re-read is the one that would otherwise wipe an item ticked a moment
        // after this one.
        if (failed.length === 0) refresh();
      })();
    },
    [day, pin, updateDay, write, refresh],
  );

  const toggleStep = useCallback(
    (itemId: string, stepId: string) => {
      if (day === null) return;
      const item = findItem(day, itemId);
      const step = item?.steps?.find((s) => s.id === stepId);
      if (step === undefined) return;

      const next = !step.done;
      const dayKey = day.day;
      const setStep = (done: boolean) =>
        updateDay(dayKey, (cached) => withStepDone(cached, itemId, stepId, done));

      setStep(next);
      void write(dayKey, [stepId], next, () => setStep(step.done));
    },
    [day, updateDay, write],
  );

  /**
   * Pinned against the account's TODAY rather than the day on screen, because
   * that is the day a new reminder lands on whatever the chooser is pointing at
   * - a reminder carries no anchor day of its own (`createReminder`).
   *
   * A pin for an id no day payload holds is inert: `remindersOnDay` only appends
   * ids it finds among that day's reminders. So this is safe to say from
   * Yesterday or Tomorrow, where the row genuinely is not - and where the toast
   * already says "It is not on 13 August." instead.
   */
  const pinReminder = useCallback(
    (reminderId: string) => {
      if (today === null) return;
      pin(today, reminderId);
    },
    [pin, today],
  );

  // Forgetting the day is what asks for it again: the effect above sends a
  // request for exactly the day that has no entry.
  const retry = useCallback(() => {
    forget(askAgainFor);
  }, [forget, askAgainFor]);

  const activateToday = useCallback(() => {
    setChoice('today');
    if (writesOut.current > 0) {
      pendingRefresh.current = 'today';
      return;
    }
    invalidateToday();
  }, [invalidateToday]);

  const choose = useCallback(
    (next: DayChoice) => {
      if (next !== choice) {
        setChoice(next);
        return;
      }
      // Choosing the day already on screen asks for it again when it could not
      // be read, and does nothing at all when it is here.
      if (failure !== null) forget(askAgainFor);
    },
    [choice, failure, forget, askAgainFor],
  );

  const signOut = useCallback(() => {
    setSignedOut(true);
  }, []);

  return {
    day,
    dayKey: target.status === 'day' ? target.day : null,
    today,
    ready: today !== null,
    loading,
    failure,
    signedOut,
    firstRun,
    notice,
    future: day !== null && today !== null && isAfter(day.day, today),
    choice,
    choose,
    activateToday,
    retry,
    toggleItem,
    toggleStep,
    toggleReminder,
    pinnedReminders: (day === null ? undefined : pinned[day.day]) ?? NO_PINS,
    pinReminder,
    say,
    amendNotice,
    dismiss,
    refresh,
    revision: generation,
    completions,
    signOut,
  };
}
